import QuickComposeAttendeeRows from './QuickComposeAttendeeRows';
import QuickComposeCollabSpaceRow from './QuickComposeCollabSpaceRow';
import QuickComposeLocationWell from './QuickComposeLocationWell';
import TimeComponent from './TimeComponent';
import TitleAndSuggestionsWithCharm from './TitleAndSuggestionsWithCharm';
import { LazyQuickComposeEditor } from '../contentsIndex';
import getComposeStyles from '../utils/getComposeStyles';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { CalendarChannelPicker } from 'owa-calendar-channel-picker';
import { lazyShowFullCompose } from 'owa-calendar-compose-form-store';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import canChangeCalendar from 'owa-calendar-compose-form-store/lib/utils/canChangeCalendar';
import { hasAttendees, isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import AllDayToggle from 'owa-calendar-forms-common/lib/components/AllDayToggle';
import getGroupAsAttendee from 'owa-calendar-forms-common/lib/utils/getGroupAsAttendee';
import {
    CharmSelectionType,
    FULL_COMPOSE_VIEW_TYPE_MODAL,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { OnlineMeetingElement } from 'owa-calendar-meeting-forms-common-view';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { invitationWontBeSentToGroup } from 'owa-locstrings/lib/strings/invitationwontbesenttogroup.locstring.json';
import { sendEventToAllGroupMembers } from 'owa-locstrings/lib/strings/sendeventtoallgroupmembers.locstring.json';
import { lazyCanTravelToEvent, MobilityFullComposeRow } from 'owa-mobility-compose';
import { getOnlineMeetingIconName } from 'owa-online-meeting';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import * as React from 'react';
import {
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
    QuickComposeControl,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    onCalendarIdChanged,
    onChannelChanged,
    onRequiredAttendeesChanged,
    logQuickComposeControlUsed,
    updateAllDay,
} from 'owa-calendar-compose-form-actions';
import {
    openSchedulingAssistant,
    initializeSchedulingAssistant,
} from 'owa-calendar-scheduling-assistant';

const styles = require('./QuickComposeContent.scss');
const classNames = require('classnames/bind').bind(styles);

export interface EventContentProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    onShowFullCompose?: () => void;
}

export default observer(function EventContent(props: EventContentProps) {
    /**
     * Adds the group as an attendee to the event and Opens full compose
     */
    const addGroupAttendeeAndOpenFullCompose = e => {
        logUsage('inviteGroupMembersClicked', ['QuickCompose']);
        let mergedAttendees: AttendeeType[] = [];
        mergedAttendees.push(...props.event.RequiredAttendees);
        mergedAttendees.push(getGroupAsAttendee(props.event));

        onRequiredAttendeesChanged(mergedAttendees);
        openFullCompose(props.onShowFullCompose);
    };
    const { event, colorScheme } = props;
    const { viewType } = getQuickComposeViewState();
    const calendarTheme = themeFromColorScheme(colorScheme);
    // This function is to support cal-css-variables feature flag and generate the expected style
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    const isTimePanelViewType = viewType == 'TimePanel';
    const isSurfaceViewType = viewType == 'Surface';
    const showCalendarChannelPicker = canChangeCalendar();
    const onlineMeetingIcon = getOnlineMeetingIconName(event);
    const calendarPickerCalloutStyle = isTimePanelViewType
        ? styles.calendarChannelPickerCalloutTimePanel
        : isQuickComposeInEditMode()
        ? styles.calendarChannelPickerCalloutEditPeek
        : null;
    const showOnlineMeetingElement =
        onlineMeetingIcon &&
        (isTimePanelViewType ||
            (isSurfaceViewType &&
                isFeatureEnabled('cal-mf-onlineMeetingQuickCompose') &&
                hasAttendees(event, true /* ignoreResources */)));

    let showMobilityCompose = false;
    if (isFeatureEnabled('cal-mf-mobility')) {
        const canTravelToEvent = lazyCanTravelToEvent.tryImportForRender();

        if (canTravelToEvent) {
            showMobilityCompose = canTravelToEvent(event);
        }
    }

    const handleTimeSuggestionNotFound = () => {
        onTimeSuggestionNotFound(props.onShowFullCompose);
    };

    return (
        <div
            className={classNames(styles.basicQuickComposeContainer, {
                isCalendarPickerAboveTitle: showCalendarChannelPicker && isTimePanelViewType,
            })}>
            {showCalendarChannelPicker && (
                <div
                    className={classNames(
                        styles.composeRow,
                        styles.calendarChannelPickerContainer,
                        {
                            isTimePanelViewType: isTimePanelViewType,
                        }
                    )}>
                    <CalendarChannelPicker
                        event={event}
                        colorScheme={colorScheme}
                        styles={{
                            textField: styles.calendarChannelPickerTextField,
                            textFieldRoot: !isTimePanelViewType
                                ? styles.calendarPickerTextFieldRoot
                                : styles.calendarPickerTextFieldRootTimePanel,
                            callout: calendarPickerCalloutStyle,
                            optionsContainer: !isTimePanelViewType
                                ? styles.calendarPickerOptionsContainerPeek
                                : null,
                        }}
                        onCalendarIdChanged={onCalendarIdChanged}
                        onChannelChanged={onChannelChanged}
                    />
                </div>
            )}
            <div className={styles.titleContainer}>
                <TitleAndSuggestionsWithCharm colorScheme={colorScheme} isInEditMode={false} />
                {!isTimePanelViewType && (
                    <AllDayToggle
                        isAllDay={event.IsAllDayEvent}
                        onAllDayToggled={onAllDayToggled}
                        colorScheme={colorScheme}
                    />
                )}
            </div>
            <QuickComposeAttendeeRows
                colorScheme={colorScheme}
                theme={calendarTheme}
                showAvailability={!isConsumer()}
            />
            <TimeComponent
                isInEditMode={false}
                colorScheme={colorScheme}
                event={event}
                isTimePanelViewType={isTimePanelViewType}
                onTimeSuggestionNotFound={handleTimeSuggestionNotFound}
            />
            {isTimePanelViewType && (
                <div className={classNames(styles.composeRow, styles.allDayRow)}>
                    <AllDayToggle
                        isAllDay={event.IsAllDayEvent}
                        onAllDayToggled={onAllDayToggled}
                        colorScheme={colorScheme}
                    />
                </div>
            )}
            <div
                className={classNames(styles.composeRow, styles.locationComposeRow, {
                    isTimePanelViewType: isTimePanelViewType,
                })}>
                <Icon iconName={ControlIcons.POI} className={styles.locationRowIcon} />
                <QuickComposeLocationWell />
            </div>
            {showMobilityCompose && (
                <div className={classNames(styles.composeRow, styles.mobilityComposeRow)}>
                    <MobilityFullComposeRow event={event} entrySource={'QuickCompose_New'} />
                </div>
            )}
            {showOnlineMeetingElement && (
                <div
                    className={classNames(
                        styles.composeRow,
                        isTimePanelViewType ? styles.onlineMeetingRow : undefined
                    )}>
                    <Icon
                        iconName={onlineMeetingIcon}
                        className={classNames(styles.onlineMeetingIcon, {
                            isSurfaceViewType: isSurfaceViewType,
                        })}
                    />
                    <OnlineMeetingElement
                        event={event}
                        calendarColorScheme={colorScheme}
                        theme={calendarTheme}
                        compactMode={isSurfaceViewType}
                        dropdownContainerClassName={
                            isTimePanelViewType ? styles.onlineMeetingDropdownContainer : undefined
                        }
                        hideDropdownTitleIcon={true}
                    />
                </div>
            )}
            <LazyQuickComposeEditor colorScheme={colorScheme} />
            {isSurfaceViewType && (
                <QuickComposeCollabSpaceRow
                    event={event}
                    composeStyles={composeGeneratedStyles}
                    openFullCompose={openFullCompose}
                    onShowFullCompose={props.onShowFullCompose}
                    customClassName={styles.eventContentCollabSpace}
                />
            )}
            {isOnGroupCalendar(event) && (
                <div className={classNames(styles.composeRow, styles.groupCalendarRowContainer)}>
                    <Icon iconName={ControlIcons.Info} className={styles.groupCalendarRowIcon} />
                    <div className={styles.groupCalendarMessageContainer}>
                        {loc(invitationWontBeSentToGroup)}
                        <Link
                            styles={composeGeneratedStyles.linkGetStyles}
                            onClick={addGroupAttendeeAndOpenFullCompose}
                            className={classNames(
                                styles.infoBarMessageLine,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}>
                            {loc(sendEventToAllGroupMembers)}
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
});

/**
 * Event handler when more details link is clicked to open full compose
 */
function openFullCompose(onShowFullCompose?: () => void, onFullComposeLoaded?: () => void) {
    if (onShowFullCompose) {
        onShowFullCompose();
    } else {
        const storeViewState = getQuickComposeViewState();
        lazyShowFullCompose
            .importAndExecute(
                storeViewState.charmSelection == CharmSelectionType.Manual,
                FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
                FULL_COMPOSE_VIEW_TYPE_MODAL
            )
            .then(() => onFullComposeLoaded?.());
    }
}

/**
 * Callback handler when time suggestion is not found
 */
async function onTimeSuggestionNotFound(onShowFullCompose?: () => void) {
    if (onShowFullCompose) {
        onShowFullCompose();
    } else {
        const storeViewState = getQuickComposeViewState();
        // Await scheduling assistant package import before showing compose
        // to ensure compose  is not displayed first while scheduling assistant loads
        await initializeSchedulingAssistant.import();
        await lazyShowFullCompose.importAndExecute(
            storeViewState.charmSelection == CharmSelectionType.Manual,
            FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
            FULL_COMPOSE_VIEW_TYPE_MODAL
        );
        openSchedulingAssistant(false /* isProposeNewTime */);
    }
}

function onAllDayToggled(isChecked) {
    logQuickComposeControlUsed(QuickComposeControl.AllDay, true /* isFocus */);
    updateAllDay(isChecked, true /* isAllDayEventExplicitlySet */);
}
